import { useEffect, useRef } from "react";
import { Rank, Suit, type Card, type CbTableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal, useSignal, type Signal } from "../net/signal";
import { CardView } from "../ui/Card";
import { CbScene } from "../cb/scene";
import { CbOnline } from "../cb/controller";
import { Overlay } from "../ui/Overlay";
import { MatchOver } from "./MatchOver";
import { unitName } from "./watching";
import { fa } from "./cards";

/**
 * میزِ چهاربرگ — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه (CbScene) و کنترلر (CbOnline) پورتِ CharBargView و CharBargOnlineController
 * یونیتی‌اند و DOM را خودشان می‌کشند؛ این کامپوننت فقط ظرف را می‌دهد و کرومِ
 * دورِ میز را: انتخابگرِ ترکیب، پایانِ دور و مسابقه.
 */
export function CharBargTable({ state }: { state: CbTableState }) {
  const host = useRef<HTMLDivElement>(null);
  const sig = useRef<Signal<CbOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<CbOnline | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: CbOnline | null = null;
    const scene = new CbScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      seatName: (l) => c?.seatName(l) ?? "حریف",
    });
    c = new CbOnline(scene);
    sig.current!.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); sig.current!.value = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host cbg" ref={host}>
      <Chrome sig={sig.current} />
    </div>
  );
}

function Chrome({ sig }: { sig: Signal<CbOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ChromeInner c={c} />;
}

function ChromeInner({ c }: { c: CbOnline }) {
  const overlay = useSignal(c.overlay);
  const picker = useSignal(c.picker);
  const st = c.state;
  // واحدِ من همیشه ۰ است (نقشه‌ی محلی) — ولی تماشاچی «من» ندارد.
  const won = (u: number) => !st.watching && u === 0;
  return (
    <>
      {picker && !st.matchOver && <CapturePicker played={picker.played} options={picker.options} best={picker.best} onPick={(i) => c.pickCapture(i)} />}

      {overlay?.kind === "round" && (
        <Overlay label="پایانِ دور">
          <div style={{ fontSize: 20, fontWeight: 700, color: won(overlay.unit) ? "var(--gold-bright)" : "#ff8d80" }}>
            {won(overlay.unit)
              ? (st.teams ? "این دور را بردیم!" : "این دور را بردی!")
              : `${st.watching ? unitName(overlay.unit, st.teams) : st.teams ? "تیمِ حریف" : c.seatName(overlay.unit)} این دور را برد`}
          </div>
          <div className="muted">امتیازِ این دور: {overlay.roundScore.map(fa).join(" — ")} · جمع: {st.score.map(fa).join(" — ")} (تا {fa(st.targetScore)})</div>
          <button className="btn" onClick={() => c.nextRound()}>دورِ بعد</button>
        </Overlay>
      )}

      {overlay?.kind === "match" && <MatchOver score={st.score} me={0} units={st.units} watching={st.watching} />}
    </>
  );
}

/**
 * وقتی یک کارت چند جورِ برداشت دارد، سرور تصمیم نمی‌گیرد و می‌پرسد —
 * همتای OpenCapturePicker اپ: هر ترکیب یک ردیفِ قابلِ لمس، بهترین طلایی.
 */
function CapturePicker({ played, options, best, onPick }: { played: Card; options: Card[][]; best: number; onPick(i: number): void }) {
  return (
    <Overlay label="انتخابِ ترکیبِ برداشت">
      <div style={{ fontSize: 17, fontWeight: 700, color: "var(--gold-bright)" }}>
        با {CbScene.cardLabel(played)} کدام ترکیب را می‌بری؟
      </div>
      <div className="stack opts">
        {options.map((opt, i) => (
          <button key={i} className={`opt ${i === best ? "best" : ""}`} onClick={() => onPick(i)}>
            <span className="cards">
              {opt.map((k) => <CardView key={`${k.suit}-${k.rank}`} card={k} width={40} />)}
            </span>
            <span className="tag">
              <b>{fa(opt.length)} برگ</b>
              {labelsOf(opt, i === best) && <i>{labelsOf(opt, i === best)}</i>}
            </span>
          </button>
        ))}
      </div>
    </Overlay>
  );
}

/** برچسبِ ردیفِ ترکیب: کارت‌های امتیازی و «بهترین» (بهترین را سرور می‌گوید). */
function labelsOf(cards: { suit: Suit; rank: Rank }[], best: boolean): string {
  const out: string[] = [];
  if (cards.some((c) => c.rank === Rank.TEN && c.suit === Suit.DIAMONDS)) out.push("۱۰ خشت");
  if (cards.some((c) => c.rank === Rank.TWO && c.suit === Suit.CLUBS)) out.push("۲ گشنیز");
  if (best) out.push("بهترین");
  return out.join(" · ");
}

void client;

export function CharBargScorebar({ state }: { state: CbTableState }) {
  const units = state.score.length;
  const teams = units === 2 && state.seats.length > units;
  return (
    <div className="scorebar">
      <div className="deckmedal" title="کارت‌های مانده در دسته">
        <span className="s">{fa(state.deckLeft)}</span>
        <span className="l">دسته</span>
      </div>
      <div className="rows">
        {state.score.map((v, u) => (
          <div key={u} className={`srow ${u === 0 ? "us" : "them"}`}>
            <span className="who">{teams ? (u === 0 ? "ما" : "حریف") : state.seats[u]?.name || "حریف"}</span>
            <span className="bar"><i style={{ width: `${Math.min(100, (v / Math.max(1, state.targetScore)) * 100)}%` }} /></span>
            <b className="num">{fa(v)}</b>
          </div>
        ))}
      </div>
      <div className="match">
        <span className="l">هدف</span>
        <b>{fa(state.targetScore)}</b>
        {state.lastDeal && <span className="l">پخشِ آخر</span>}
      </div>
    </div>
  );
}
